import moment from 'moment-timezone';
import type { HealthStatus, PlatformWidget, AlertSeverity } from "./types";
import { SEVERITY, HEALTH_STATUS } from "./Constants";


export const STATUS_META: Record<
  HealthStatus,
  { label: string; color: string; severityRank: number }
> = {
  CRITICAL: { label: SEVERITY.CRITICAL, color: "#F04438", severityRank: 1 },
  MAJOR: { label: SEVERITY.MAJOR, color: "#F79009", severityRank: 2 },
  MINOR: { label: SEVERITY.MINOR, color: "#EAAA08", severityRank: 3 },
  WARNING: { label: SEVERITY.WARNING, color: "#FFCC00", severityRank: 4 },
  INFO: { label: SEVERITY.INFO, color: "#2E90FA", severityRank: 5 },
  PLATFORM_AFFECTED: { label: SEVERITY.PLATFORM_AFFECTED, color: "#71758F", severityRank: 6 },
  HEALTHY: { label: HEALTH_STATUS.HEALTHY, color: "#01C008", severityRank: 99 },
};

export const withAlpha = (hex: string, alpha: number) => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export type Segment = {
  key: string;
  label: string;
  count: number;
  bg: string;
  severityRank: number;
  statusKey: Exclude<HealthStatus, "HEALTHY">;
  alertSeverity: AlertSeverity;
};

const safeNum = (n: any) => (typeof n === "number" && Number.isFinite(n) ? n : 0);

export const buildSegments = (w: PlatformWidget): Segment[] => {
  const c = w.alertCounts ?? ({} as any);

  return [
    {
      key: "critical",
      label: SEVERITY.CRITICAL,
      count: safeNum(c.critical),
      bg: STATUS_META.CRITICAL.color,
      severityRank: STATUS_META.CRITICAL.severityRank,
      statusKey: "CRITICAL",
      alertSeverity: "CRITICAL",
    },
    {
      key: "major",
      label: SEVERITY.MAJOR,
      count: safeNum(c.major),
      bg: STATUS_META.MAJOR.color,
      severityRank: STATUS_META.MAJOR.severityRank,
      statusKey: "MAJOR",
      alertSeverity: "MAJOR",
    },
    {
      key: "minor",
      label: SEVERITY.MINOR,
      count: safeNum(c.minor),
      bg: STATUS_META.MINOR.color,
      severityRank: STATUS_META.MINOR.severityRank,
      statusKey: "MINOR",
      alertSeverity: "MINOR",
    },
    {
      key: "warning",
      label: SEVERITY.WARNING,
      count: safeNum(c.warning),
      bg: STATUS_META.WARNING.color,
      severityRank: STATUS_META.WARNING.severityRank,
      statusKey: "WARNING",
      alertSeverity: "WARNING",
    },
    {
      key: "info",
      label: SEVERITY.INFO,
      count: safeNum(c.info),
      bg: STATUS_META.INFO.color,
      severityRank: STATUS_META.INFO.severityRank,
      statusKey: SEVERITY.INFO,
      alertSeverity: SEVERITY.INFO,
    },
    {
      key: "platformAffected",
      label: SEVERITY.PLATFORM_AFFECTED.replace("_", " "),
      count: safeNum(c?.platformAffected),
      bg: STATUS_META.PLATFORM_AFFECTED.color,
      severityRank: STATUS_META.PLATFORM_AFFECTED.severityRank,
      statusKey: "PLATFORM_AFFECTED",
      alertSeverity: "PLATFORM_AFFECTED",
    },
  ];
};


export const getTopSegments = (w: PlatformWidget): Segment[] => {
  return buildSegments(w)
    .filter((s) => s.count > 0)
    .sort((a, b) => a.severityRank - b.severityRank)
    .slice(0, 2);
};

const deriveFromAnyCounts = (w: PlatformWidget): HealthStatus => {
  const segs = buildSegments(w).filter((s) => s.count > 0);
  if (segs.length === 0) return "HEALTHY";

  const highest = segs.reduce((best, cur) =>
    cur.severityRank < best.severityRank ? cur : best, segs[0]
  );
  return highest.statusKey;
};

export const deriveStatusKey = (w: PlatformWidget): HealthStatus => {
  const total = safeNum(w.alertCounts?.total);

  if (total > 0) {
    const top = getTopSegments(w);
    if (top.length > 0) {
      return top[0].statusKey; 
    }
    return deriveFromAnyCounts(w);
  }

  const backendStatus = (w.status ?? "").toUpperCase() as HealthStatus;
  if (backendStatus && backendStatus !== "HEALTHY" && STATUS_META[backendStatus]) {
    return backendStatus;
  }

  return "HEALTHY";
};

type PreviewAlert = NonNullable<PlatformWidget["previewAlerts"]>[number];

const isNotAlreadyPicked = (picked: PreviewAlert[], a: PreviewAlert) =>
  !picked.some((p) => p.alertId === a.alertId);

const fillBySeverity = (
  alerts: PreviewAlert[],
  picked: PreviewAlert[],
  sev: AlertSeverity,
  limit: number
) => {
  for (const a of alerts) {
    if (picked.length >= limit) break;
    if (a.severity === sev) picked.push(a);
  }
};

const fillRemaining = (alerts: PreviewAlert[], picked: PreviewAlert[], limit: number) => {
  for (const a of alerts) {
    if (picked.length >= limit) break;
    if (isNotAlreadyPicked(picked, a)) picked.push(a);
  }
};

const pickForOneSeg = (alerts: PreviewAlert[], top: Segment[], limit: number): PreviewAlert[] => {
  const picked: PreviewAlert[] = [];
  fillBySeverity(alerts, picked, top[0].alertSeverity, limit);
  fillRemaining(alerts, picked, limit);
  return picked.slice(0, limit);
};

const pickForTwoSegs = (alerts: PreviewAlert[], top: Segment[], limit: number): PreviewAlert[] => {
  const picked: PreviewAlert[] = [];
  for (const seg of top.slice(0, 2)) {
    const found = alerts.find((a) => a.severity === seg.alertSeverity);
    if (found && isNotAlreadyPicked(picked, found)) picked.push(found);
  }
  fillRemaining(alerts, picked, limit);
  return picked.slice(0, limit);
};

export const pickPreviewAlerts = (w: PlatformWidget, limit = 2) => {
  const alerts = [...(w.previewAlerts ?? [])];
  if (alerts.length === 0) return [];

  const top = getTopSegments(w);

  if (top.length === 1) return pickForOneSeg(alerts, top, limit);
  if (top.length >= 2) return pickForTwoSegs(alerts, top, limit);

  return alerts.slice(0, limit);
};

export const getRemainingAlertsCount = (w: PlatformWidget, previewShown: number) => {
  const total = safeNum(w.alertCounts?.total);
  const remaining = total - safeNum(previewShown);
  return Math.max(remaining, 0);
};

export const TIME_RANGES = [
  { label: "Today", value: "today" },
  { label: "Last 1 min", value: "1m" },
  { label: "Last 5 mins", value: "5m" },
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
] as const;

export const AUTO_REFRESH_OPTIONS = [
  { label: "No Auto Refresh", value: 0 },
  { label: "5 Mins", value: 5 },
  { label: "10 Mins", value: 10 },
  { label: "20 Mins", value: 20 },
  { label: "30 Mins", value: 30 },
] as const;

const TIME_RANGE_OFFSETS: Record<string, { unit: "m" | "h" | "d" | "today"; amount: number }> = {
  "today": { unit: "today", amount: 0 },
  "24h":   { unit: "h", amount: 24 },
  "7d":    { unit: "d", amount: 7 },
  "30d":   { unit: "d", amount: 30 },
  "90d":   { unit: "d", amount: 90 },
};

const pad = (n: number) => String(n).padStart(2, "0");

const formatDateDisplay = (d: Date): string => {
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const getTimeRangeDates = (
  timeRange: string
): { startLabel: string; endLabel: string } => {
  const end = moment.tz('America/Chicago').endOf('day');
  let start = moment.tz('America/Chicago').startOf('day');
  
  const offset = TIME_RANGE_OFFSETS[timeRange];

  if (offset) {
    if (offset.unit === "h") {
      start = moment.tz('America/Chicago').subtract(offset.amount, 'hours').startOf('day');
    } else if (offset.unit === "d") {
      start = moment.tz('America/Chicago').subtract(offset.amount, 'days').startOf('day');
    }
  } else {
    // Fallback: 30 days snapped
    start = moment.tz('America/Chicago').subtract(30, 'days').startOf('day');
  }

  return {
    startLabel: start.format("MM-DD-YYYY HH:mm"),
    endLabel: end.format("MM-DD-YYYY HH:mm"),
  };
};
